<template>
  <div>
    <h4 class="mb-1">
      Запросы от пользователей
    </h4>
    <p class="text-muted">
      Пользователь не нашёл старт в поиске и описал соревнование своими словами. Если к запросу приложен результат
      (время и подтверждение), после проверки его можно засчитать в статистику пользователя.
    </p>
    <b-alert
      v-if="errorText"
      show
      variant="danger"
    >
      {{ errorText }}
    </b-alert>
    <b-form-select
      v-model="status"
      :options="statusOptions"
      class="w-25 mb-1"
      @change="fetch"
    />
    <b-table
      :items="rows"
      :fields="fields"
      :busy="loading"
      striped
      responsive
      empty-text="Запросов нет"
    >
      <template #cell(user)="data">
        <span v-if="data.item.user">{{ data.item.user.surname }} {{ data.item.user.name }}</span>
      </template>
      <template #cell(result)="data">
        <template v-if="data.item.result">
          <strong>{{ data.item.result.text }}</strong>
          <b-badge
            :variant="decisionVariant(data.item.result.decision)"
            class="ml-50"
          >
            {{ decisionLabel(data.item.result.decision) }}
          </b-badge>
        </template>
        <span v-else>—</span>
      </template>
      <template #cell(description)="data">
        <span class="d-inline-block text-truncate" style="max-width: 260px">
          {{ data.item.description || '—' }}
        </span>
      </template>
      <template #cell(actions)="data">
        <b-button
          size="sm"
          variant="outline-info"
          class="mr-50"
          @click="openDetails(data.item)"
        >
          Прочитать
        </b-button>
        <b-button
          v-if="data.item.status !== 'IN_PROGRESS' && data.item.status !== 'CLOSED'"
          size="sm"
          variant="outline-primary"
          class="mr-50"
          @click="setStatus(data.item, 'IN_PROGRESS')"
        >
          В работу
        </b-button>
        <b-button
          v-if="data.item.status !== 'CLOSED'"
          size="sm"
          variant="outline-secondary"
          @click="setStatus(data.item, 'CLOSED')"
        >
          Закрыть
        </b-button>
      </template>
    </b-table>

    <b-modal
      v-model="detailsOpen"
      title="Сообщение пользователя"
      ok-only
      ok-title="Закрыть"
      size="lg"
    >
      <template v-if="selected">
        <p class="mb-50"><strong>Пользователь:</strong> {{ userName(selected) }}</p>
        <p class="mb-50"><strong>Статус:</strong> {{ statusLabel(selected.status) }}</p>
        <p class="mb-50"><strong>Название старта:</strong> {{ selected.title || '—' }}</p>
        <p class="mb-50"><strong>Дата:</strong> {{ selected.dateText || '—' }}</p>
        <p class="mb-50"><strong>Место:</strong> {{ selected.place || '—' }}</p>
        <p class="mb-50"><strong>Дистанция:</strong> {{ selected.distance || '—' }}</p>
        <p class="mb-50"><strong>Дата рождения:</strong> {{ selected.birthday || '—' }}</p>
        <template v-if="selected.result">
          <hr>
          <p class="mb-50"><strong>Результат:</strong> {{ selected.result.text }} · {{ distanceText(selected.result) }}</p>
          <p class="mb-50"><strong>Дата старта:</strong> {{ selected.result.eventDate || '—' }}</p>
          <p class="mb-50">
            <strong>Подтверждение:</strong>&nbsp;<a
              v-if="selected.result.proofUrl"
              :href="selected.result.proofUrl"
              target="_blank"
              rel="noopener"
            >ссылка на результат</a>
            <span v-if="selected.result.proofUrl && selected.result.proofFile"> · </span>
            <a
              v-if="selected.result.proofFile"
              :href="selected.result.proofFile.url"
              target="_blank"
              rel="noopener"
            >{{ selected.result.proofFile.name || 'файл протокола' }}</a>
          </p>
          <p class="mb-50">
            <strong>Решение:</strong> {{ decisionLabel(selected.result.decision) }}
            <span v-if="selected.result.decisionComment"> — {{ selected.result.decisionComment }}</span>
          </p>
          <b-form-input
            v-model="decisionComment"
            placeholder="Комментарий для пользователя (необязательно)"
            class="mb-1"
          />
          <b-button
            variant="success"
            class="mr-50"
            :disabled="deciding"
            @click="decide(selected, 'approved')"
          >
            Засчитать результат
          </b-button>
          <b-button
            variant="outline-danger"
            :disabled="deciding"
            @click="decide(selected, 'rejected')"
          >
            Отклонить
          </b-button>
          <hr>
        </template>
        <p class="mb-50"><strong>Сообщение:</strong></p>
        <p class="mb-0" style="white-space: pre-wrap">{{ selected.description || 'Пользователь не оставил комментарий' }}</p>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BFormInput,
  BFormSelect,
  BModal,
  BTable,
} from 'bootstrap-vue'
import identityApi from '@/services/identity-api.service'

export default {
  name: 'MissingEventRequestsPage',
  components: {
    BAlert,
    BBadge,
    BButton,
    BFormInput,
    BFormSelect,
    BModal,
    BTable,
  },
  data() {
    return {
      loading: false,
      errorText: '',
      status: 'NEW',
      rows: [],
      detailsOpen: false,
      selected: null,
      decisionComment: '',
      deciding: false,
      statusOptions: [
        { value: '', text: 'Все' },
        { value: 'NEW', text: 'Новые' },
        { value: 'IN_PROGRESS', text: 'В работе' },
        { value: 'CLOSED', text: 'Закрытые' },
      ],
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'status', label: 'Статус' },
        { key: 'title', label: 'Название' },
        { key: 'dateText', label: 'Дата' },
        { key: 'place', label: 'Место' },
        { key: 'result', label: 'Результат' },
        { key: 'user', label: 'Пользователь' },
        { key: 'description', label: 'Сообщение' },
        { key: 'actions', label: '' },
      ],
    }
  },
  created() {
    this.fetch()
  },
  methods: {
    async fetch() {
      this.loading = true
      this.errorText = ''
      try {
        const { data } = await identityApi.getMissingRequests({ status: this.status || undefined })
        this.rows = data.rows || []
      } catch (e) {
        this.errorText = e.response?.data?.message || 'Ошибка загрузки'
      } finally {
        this.loading = false
      }
    },
    openDetails(item) {
      this.selected = item
      this.decisionComment = item.result?.decisionComment || ''
      this.detailsOpen = true
    },
    decisionLabel(decision) {
      const map = { approved: 'Засчитан', rejected: 'Отклонён' }
      return map[decision] || 'Ждёт проверки'
    },
    decisionVariant(decision) {
      const map = { approved: 'light-success', rejected: 'light-danger' }
      return map[decision] || 'light-warning'
    },
    distanceText(result) {
      if (!result.distanceM) return this.selected?.distance || 'дистанция не указана'
      const km = result.distanceM / 1000
      return `${String(km).replace('.', ',')} км${result.sport && result.sport !== 'run' ? ` (${result.sport})` : ''}`
    },
    async decide(item, decision) {
      this.deciding = true
      this.errorText = ''
      try {
        const { data } = await identityApi.decideMissingResult(item.id, decision, this.decisionComment || undefined)
        this.selected = data
        this.fetch()
      } catch (e) {
        this.errorText = e.response?.data?.message || 'Не удалось сохранить решение'
      } finally {
        this.deciding = false
      }
    },
    userName(item) {
      if (!item.user) return '—'
      return `${item.user.surname || ''} ${item.user.name || ''}`.trim()
    },
    statusLabel(status) {
      const map = {
        NEW: 'Новый',
        IN_PROGRESS: 'В работе',
        CLOSED: 'Закрыт',
      }
      return map[status] || status
    },
    async setStatus(item, status) {
      try {
        await identityApi.updateMissingRequest(item.id, status)
        this.fetch()
      } catch (e) {
        this.errorText = e.response?.data?.message || 'Не удалось обновить статус'
      }
    },
  },
}
</script>
